import {computed, defineComponent, onMounted, onUnmounted, ref, watch} from "vue";
import * as KlipApi from "@/api/klip-api.proxy";
import {
    CurrentInvoiceRound,
    InvoiceStates,
    InvoicingRoundStartMethod,
    LogTypes,
    MapRequestWithFinancialAnomalies
} from "@/api/klip-api.proxy";
import {ISelectedOrganisation} from "@/app/admin/components/kl-search-organisation/kl-search-organisation";
import {AdminMapRequestWithFinancialAnomalies} from "@/app/admin/admin-routes";
import {useForm} from 'vee-validate';
import {RouteRecordRaw} from 'vue-router';
import {useKlipApiProxy} from '@/plugins/proxy-client';
import DateUtil from '@/app/shared/helpers/date-util';
import {first} from 'lodash-es';
import * as yup from 'yup';
import {toTypedSchema} from '@vee-validate/yup';
import {
    klValidationFields,
    ValidationFieldsType
} from '@/app/shared/components/kl-form-fields/kl-field-validation-util';
import {useCodeListStore} from "@/app/shared/state/CodeListModule";
import {useUserStore} from "@/app/shared/state/UserDataModule";
import {storeToRefs} from "pinia";

export default defineComponent({
    compatConfig: {
        MODE: 3,
    },
    name: 'KlStartInvoicing',
    setup() {

        const schema = yup.object({
            selectedOrganisationTpe: yup.string().required(),
            selectedOrganisations: yup.array().notRequired()
                .when('selectedOrganisationTpe', { is: 'select', then: schema => schema.required().min(1) }),
            dateFrom: yup.array().kl_required_date().label('Alle aanvragen tot en met'),
        });

        const isBeta = computed(() => {
            return useCodeListStore().isBeta;
        });

        const { user } =  storeToRefs(useUserStore());
        const klipApiProxy = useKlipApiProxy();
        const today = DateUtil.formatDate(DateUtil.now());

        const form = useForm({
            validationSchema: toTypedSchema(schema),
            initialValues: {
                selectedOrganisationTpe: 'all',
                selectedOrganisations: [],
                dateFrom: [today]
            },
        });
        const fields: ValidationFieldsType<typeof schema> = klValidationFields<typeof schema>(schema);

        const selectedOrganisationTpe = fields.selectedOrganisationTpe.getValue<string>();
        const selectedOrganisations = fields.selectedOrganisations.getValue<ISelectedOrganisation[]>();
        const dateFrom = fields.dateFrom.getValue<string[]>();

        const next6thDay = ref<string>('');
        const invoiceFormVisible = ref<boolean>(false);
        const dryRunOnly = ref<boolean>(false);

        const disableSubmit = computed((): boolean => {
            return startingNewRound.value || !form.meta.value.valid;
        })

        let invoiceRoundintervalId: NodeJS.Timeout = null;
        const roundSuccessfullyDone = ref<boolean>(false);
        const hasErrors = ref<boolean>(false);
        const startingNewRound = ref<boolean>(false);
        const overruleDryRun = ref<boolean>(false);
        const isSending = ref<boolean>(false);
        const organisationDataFetching = ref<boolean>(false);
        const dateFromMaxDate = ref<string>(today);
        const organisationSuggestion = ref<ISelectedOrganisation[]>([]);
        const currentInvoiceRound = ref<KlipApi.CurrentInvoiceRound>(null);
        const currentInvoiceRoundId = ref<string>('');
        const loading = ref<boolean>(true);
        const error = ref<string>('');
        const nothingToInvoice = ref<boolean>(false);
        const mapRequestsNotRegisteredInFinancialTransaction = ref<MapRequestWithFinancialAnomalies[]>([]);
        const organisationSelection = computed((): boolean => {
            return selectedOrganisationTpe.value === 'select';
        });

        const routeAdminMapRequestWithFinancialAnomalies = computed((): RouteRecordRaw => {
            return AdminMapRequestWithFinancialAnomalies;
        });

        const title = computed(() => {
            if (noActiveRoundRunning.value) {
                return 'Facturatieronde';
            } else {
                return `Facturatieronde van ${formatDate(currentInvoiceRound.value.startTime)} - ${currentInvoiceRoundId.value}`
            }
        });

        const pollInvoiceRound = async () => {
            const response = await klipApiProxy.invoice_CurrentInvoiceRound();
            if (response.isSuccessStatusCode && response.result !== null) {
                if (response.result.state === InvoiceStates.InvoicesSent ||
                    response.result.state === InvoiceStates.Completed) {
                    roundSuccessfullyDone.value = true;
                    hasErrors.value = response.result.log.some(l => l.logTypes === LogTypes.Error) && !response.result.invoicedRunId;
                }
                currentInvoiceRound.value = response.result;
            }
        };

        const noActiveRoundRunning = computed(() => {
            return (currentInvoiceRound.value === null || currentInvoiceRound.value === undefined) ||
                currentInvoiceRound.value.state === InvoiceStates.InvoicesSent || currentInvoiceRound.value.state === InvoiceStates.Completed ||
                startingNewRound.value;
        });

        const formatDate = (timestamp: string) => {
            return DateUtil.formatDateTime(timestamp);
        };

        const _onFormSubmit = async () => {
            startingNewRound.value = true;
            roundSuccessfullyDone.value = false;
            invoiceFormVisible.value = false;

            error.value = '';

            const invoiceRoundData: KlipApi.IStartInvoicingRoundRequest = {
                startDate: DateUtil.formatForApi(DateUtil.parseToDayJs(dateFrom.value).add(1, 'day'), 'ISO'),
                startMethod: dryRunOnly.value ? InvoicingRoundStartMethod.DryRunOnly : (overruleDryRun.value ? InvoicingRoundStartMethod.DryRunOverruleAndStart : InvoicingRoundStartMethod.Default),
                organisations: isBeta.value ? [user.value.klipOrganisationApiId] : selectedOrganisations.value?.map(s => s.organisationId),
            };

            const startInvoiceRoundResult = await klipApiProxy.invoice_StartInvoiceRound(KlipApi.StartInvoicingRoundRequest.fromJS(invoiceRoundData));

            if (startInvoiceRoundResult.result && startInvoiceRoundResult.result.success) {
                if (!!startInvoiceRoundResult.result.invoiceRoundId) {
                    currentInvoiceRoundId.value = startInvoiceRoundResult.result.invoiceRoundId;
                    currentInvoiceRound.value = new CurrentInvoiceRound();
                    startingNewRound.value = false;
                    invoiceRoundintervalId = setInterval(pollInvoiceRound, 5000);
                } else {
                    nothingToInvoice.value = true;
                }
            } else {
                error.value = startInvoiceRoundResult.result.failure.errors[0].message;
            }

            startingNewRound.value = false;
        };

        const logClass = (log: KlipApi.InvoicingLogMessage) => {
            return [
                'kl-invoice-alert', {
                    'kl-invoice-alert--error': log.logTypes === LogTypes.Error,
                    'kl-invoice-alert--warning': log.logTypes === LogTypes.Warning,
                    'kl-invoice-alert--success': log.logTypes === LogTypes.Info,
                }];
        }

        const onDateFromChanged = async (d: string[]) => {
            const from = DateUtil.formatForApi(first(d), 'YYYY/MM/DD');
            const result = await klipApiProxy.invoice_MapRequestsNotRegisteredInFinancialTransaction(from);
            mapRequestsNotRegisteredInFinancialTransaction.value = result.result;
        };

        const calculateNext6thDay = () => {
            const today = DateUtil.now();
            const currentMonth6th = today.date(6);
            const next6th = today.date() < 6 ? currentMonth6th : currentMonth6th.add(1, "month");
            next6thDay.value = next6th.format("DD/MM/YYYY");
        };

        const showInvoiceForm = () => {
            invoiceFormVisible.value = true;
            dryRunOnly.value = false;
        }

        const showInvoiceFormDryRun = () => {
            invoiceFormVisible.value = true;
            dryRunOnly.value = true;
        }

        onMounted(async () => {
            calculateNext6thDay();

            const currentRoundResult = await klipApiProxy.invoice_CurrentInvoiceRound();


            if (currentRoundResult.isSuccessStatusCode) {
                currentInvoiceRound.value = currentRoundResult.result;
                hasErrors.value = currentRoundResult.result.log.some(l => l.logTypes === LogTypes.Error) && !currentRoundResult.result.invoicedRunId;

                // support old and new flow
                if (currentInvoiceRound.value.state !== InvoiceStates.InvoicesSent &&
                    currentInvoiceRound.value.state !== InvoiceStates.Completed) {
                    invoiceRoundintervalId = setInterval(pollInvoiceRound, 5000);
                } else {
                    roundSuccessfullyDone.value = true;
                }
            }

            loading.value = false;
        });

        onUnmounted(() => {
            clearInterval(invoiceRoundintervalId);
        });

        watch(dateFrom, onDateFromChanged, { immediate: true });

        const _onInvalidSubmit = (submittedValues) => {
            console.log('_onInvalidSubmit', submittedValues);
        }

        const onSubmit = form.handleSubmit(_onFormSubmit, _onInvalidSubmit);

        return {
            dateFrom,
            isBeta,
            isSending,
            formatDate,
            dateFromMaxDate,
            organisationSuggestion,
            selectedOrganisationTpe,
            selectedOrganisations,
            organisationSelection,
            organisationDataFetching,
            currentInvoiceRound,
            currentInvoiceRoundId,
            noActiveRoundRunning,
            roundSuccessfullyDone,
            hasErrors,
            startingNewRound,
            overruleDryRun,
            title,
            loading,
            mapRequestsNotRegisteredInFinancialTransaction,
            routeAdminMapRequestWithFinancialAnomalies,
            logClass,
            onSubmit,
            disableSubmit,
            nothingToInvoice,
            error,
            showInvoiceForm,
            showInvoiceFormDryRun,
            invoiceFormVisible,
            next6thDay,
            dryRunOnly,
            fields,
        };
    }
});
